import React from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from '../../../../components/ui/Select';
import { findTemplate, templatesOfChannel } from '../utils/reminderTemplates';

// Valor del <select> para `null` (la plantilla por defecto o la del sistema, según dónde se use).
const NONE = '';

/**
 * Selector de plantilla de un canal. `null` es la opción "sin plantilla propia" y su rótulo
 * depende del lugar (`nullLabel`): en un paso es la plantilla por defecto del canal; en la
 * plantilla por defecto, la del sistema.
 *
 * Un id guardado que ya no está entre las activas (archivada en una carrera) se conserva
 * como opción, rotulado, en vez de cambiarlo en silencio. Sin catálogo cargado no se sabe
 * qué plantillas hay: el selector queda deshabilitado mostrando lo guardado.
 *
 * `ariaLabel` (opcional) reemplaza al rótulo visible como nombre accesible, cuando el rótulo
 * solo se entiende por su contexto (p. ej. "Correo" dentro de "Plantillas por defecto").
 *
 * @param {{ id?: string, label?: string, ariaLabel?: string, channel: 'email'|'whatsapp',
 *   value: string|null, onChange: (templateId: string|null) => void,
 *   catalog: object|null, nullLabel: string, disabled?: boolean, error?: string,
 *   hint?: string }} props `nullLabel` ya traducido
 */
export default function ReminderTemplateSelect({
  id,
  label,
  ariaLabel,
  channel,
  value,
  onChange,
  catalog,
  nullLabel,
  disabled,
  error,
  hint,
}) {
  const { t } = useTranslation();
  const templates = templatesOfChannel(catalog, channel);
  const orphan = value && !findTemplate(catalog, value);
  const orphanLabel = catalog ? t('Plantilla archivada o no disponible') : t('Plantilla guardada');

  return (
    <Select
      id={id}
      label={label}
      aria-label={ariaLabel}
      value={value ?? NONE}
      disabled={disabled || !catalog}
      error={error}
      hint={hint}
      onChange={(e) => onChange(e.target.value === NONE ? null : e.target.value)}
    >
      <option value={NONE}>{nullLabel}</option>
      {orphan && <option value={value}>{orphanLabel}</option>}
      {templates.map((template) => (
        // El nombre es texto del operador: va tal cual, sin t().
        <option key={template.id} value={template.id}>
          {template.name}
        </option>
      ))}
    </Select>
  );
}
